// src/pages/Users/Lexique/LexiquePage.jsx

import React from "react";
import { FaBookOpen } from "react-icons/fa";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Reveal from "./../../../components/Reveal/Reveal";

import useLexiqueController from "./hooks/useLexiqueController";

import LexiqueHero from "./components/LexiqueHero";
import LexiqueToolbar from "./components/LexiqueToolbar";
import LexiqueTermCard from "./components/LexiqueTermCard";
import LexiqueSkeleton from "./components/LexiqueSkeleton";
import EmptyLexiqueState from "./components/EmptyLexiqueState";

export default function LexiquePage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const {
    discipline,
    loading,
    error,
    data,
    filtered,
    groups,
    availableLetters,
    favorites,
    ui,
    searchInput,
    setSearchInput,
    updateUi,
    resetFilters,
    copyShare,
    shareCopied,
    exportCSV,
    toggleFavorite,
  } = useLexiqueController();

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title={`Lexique ${discipline || "Judo"}`}
          subtitle="Retrouve les principaux termes japonais, leur traduction, leur prononciation et ajoute tes favoris."
          icon={FaBookOpen}
          align="left"
          className="mb-4"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        <Reveal>
          <LexiqueHero
            discipline={discipline || "Judo"}
            totalCount={data.length}
            filteredCount={filtered.length}
            favoritesCount={favorites.length}
            favOnly={ui.favOnly}
          />
        </Reveal>

        <div className="sticky top-[76px] z-30 -mx-3 mt-4 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
          <LexiqueToolbar
            ui={ui}
            availableLetters={availableLetters}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onChange={updateUi}
            onReset={resetFilters}
            onShare={copyShare}
            shareCopied={shareCopied}
            onExport={exportCSV}
            filteredCount={filtered.length}
            favoritesCount={favorites.length}
          />
        </div>

        <section className="mt-5">
          {loading && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 12 }).map((_, index) => (
                <LexiqueSkeleton key={index} />
              ))}
            </div>
          )}

          {!loading && error && (
            <EmptyLexiqueState
              title="Lexique indisponible"
              description={error}
              onReset={resetFilters}
              showReset={false}
            />
          )}

          {!loading && !error && filtered.length === 0 && (
            <EmptyLexiqueState
              title="Aucun terme trouvé"
              description="Aucun mot ne correspond à ta recherche, à la lettre choisie ou aux favoris."
              onReset={resetFilters}
            />
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="space-y-6">
              {groups.map(({ letter, items }) => (
                <section key={letter} className="scroll-mt-32">
                  <header className="sticky top-[170px] z-10 -mx-3 mb-3 bg-slate-50/90 px-3 py-2 backdrop-blur dark:bg-slate-950/90 sm:static sm:mx-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
                    <div className="inline-flex items-center rounded-full bg-slate-950 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white dark:bg-white dark:text-slate-950">
                      {letter}
                    </div>
                  </header>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((row) => (
                      <LexiqueTermCard
                        key={`${row.japonais}-${row.traduction}`}
                        row={row}
                        query={ui.q}
                        isFavorite={favorites.includes(row.japonais)}
                        onToggleFavorite={() => toggleFavorite(row.japonais)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </section>
      </main>
    </section>
  );
}